import { useEffect, useState } from 'react'
import Icon from '../components/Icon'
import Toggle from '../components/Toggle'
import ConfirmDialog from '../components/ConfirmDialog'
import { fetchTenantMfaSettings, updateTenantMfaSettings } from '../api/security'
import styles from './AdminSecurityTab.module.css'

const MIN_TRUST_DAYS = 1
const MAX_TRUST_DAYS = 365

/**
 * AdminSecurityTab — Admin > Security: the tenant-wide "Require MFA" toggle
 * and its trust-device window, backed by gaussiq-identity's
 * GET/PATCH /security/tenant/mfa (tenant_props under the hood — see
 * routes/security.py). Same draft/saved/dirty save pattern as AdminSkillsTab.
 */
export default function AdminSecurityTab() {
  const [saved, setSaved] = useState(null)
  const [draft, setDraft] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [confirmingSave, setConfirmingSave] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchTenantMfaSettings()
      .then(settings => {
        if (cancelled) return
        setSaved(settings)
        setDraft(settings)
      })
      .catch(err => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const dirty = draft && saved && JSON.stringify(saved) !== JSON.stringify(draft)

  function set(key, value) {
    setDraft(prev => ({ ...prev, [key]: value }))
  }

  function step(delta) {
    set('trustDeviceDays', Math.min(MAX_TRUST_DAYS, Math.max(MIN_TRUST_DAYS, draft.trustDeviceDays + delta)))
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const settings = await updateTenantMfaSettings(draft)
      setSaved(settings)
      setDraft(settings)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  function handleSaveClick() {
    // Turning MFA on affects every user's next login (they'll be walked
    // through enrollment) — worth a confirm, same as AdminSkillsTab's
    // workspace-wide settings. Turning it off, or just changing the trust
    // window, doesn't carry the same "everyone gets interrupted" weight, but
    // one shared confirm keeps this simple and errs toward the safer prompt.
    setConfirmingSave(true)
  }

  function handleReset() {
    setDraft(saved)
  }

  if (loading) {
    return <div className={styles.wrap}><div className={`${styles.panel} ${styles.loadingText}`}>Loading…</div></div>
  }

  return (
    <div className={styles.wrap}>
      {error && (
        <div className={`${styles.panel} ${styles.errorBanner}`} role="alert">
          <span>{error}</span>
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            <Icon name="close" />
          </button>
        </div>
      )}

      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Two-factor authentication</h2>
          <p className={styles.panelSub}>Require a second sign-in step for everyone in this workspace.</p>
        </div>

        <div className={styles.row}>
          <Toggle
            label="Require MFA for this workspace"
            description="Every user must set up an authenticator app or email code before they can sign in. Users who haven't enrolled yet are walked through it on their next login."
            tone="blue"
            checked={draft.required}
            onChange={v => set('required', v)}
          />
        </div>

        <div className={styles.row}>
          <div className={styles.rowTop}>
            <div className={styles.rowCopy}>
              <span className={styles.rowLabel}>Trust this device</span>
              <p className={styles.rowDescription}>
                When a user checks "trust this device" after a successful MFA challenge, they
                won't be asked again on that device until this many days have passed.
              </p>
            </div>
            <div className={styles.stepper}>
              <button
                type="button"
                className={styles.stepperBtn}
                onClick={() => step(-1)}
                disabled={draft.trustDeviceDays <= MIN_TRUST_DAYS}
                aria-label="Decrease"
              >
                −
              </button>
              <div className={styles.stepperValue}>
                <span className={styles.stepperNumber}>{draft.trustDeviceDays}</span>
                <span className={styles.stepperUnit}>Days</span>
              </div>
              <button
                type="button"
                className={styles.stepperBtn}
                onClick={() => step(1)}
                disabled={draft.trustDeviceDays >= MAX_TRUST_DAYS}
                aria-label="Increase"
              >
                +
              </button>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <span className={styles.footerStatus}>{saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          <div className={styles.footerActions}>
            <button type="button" className={styles.resetBtn} onClick={handleReset} disabled={!dirty || saving}>
              Discard changes
            </button>
            <button type="button" className={styles.saveBtn} onClick={handleSaveClick} disabled={!dirty || saving}>
              Save changes
            </button>
          </div>
        </div>
      </div>

      {confirmingSave && (
        <ConfirmDialog
          title="Save security settings"
          message={
            draft.required && !saved.required
              ? 'Every user in this workspace will be required to set up MFA on their next login. Save changes?'
              : 'Save changes to this workspace\'s MFA settings?'
          }
          confirmLabel="Save changes"
          onConfirm={() => { setConfirmingSave(false); handleSave() }}
          onCancel={() => setConfirmingSave(false)}
        />
      )}
    </div>
  )
}
